import React, { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v1Post, v1Patch } from '@/lib/api';
import {
  validateMasterCode,
  validateMasterDisplayName,
  validateMasterCodeUnique,
  validateMasterNameUnique,
  masterDuplicateConflictField,
} from '@/lib/masterDataValidation';
import { useIdSelection } from '@/hooks/useIdSelection';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/StatusPill';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const errDetail = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const BLANK_ROLE_FORM = { code: '', name: '', status: 'active' };
const BLANK_ROLE_FIELD_ERRORS = { code: '', name: '' };

function isRoleFormDirty(form) {
  return !!form.code.trim() || !!form.name.trim() || form.status !== BLANK_ROLE_FORM.status;
}

export function RolesSection({ roles, isLoading, isError, onRequestDelete, onRegisterClearSelection }) {
  const qc = useQueryClient();
  const roleSel = useIdSelection();

  React.useEffect(() => {
    onRegisterClearSelection?.(roleSel.clear);
  }, [onRegisterClearSelection, roleSel.clear]);

  const [roleOpen, setRoleOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({ ...BLANK_ROLE_FORM });
  const [roleFieldErrors, setRoleFieldErrors] = useState({ ...BLANK_ROLE_FIELD_ERRORS });
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardConfirmedRef = useRef(false);
  const [editRole, setEditRole] = useState(null);
  const [editRoleForm, setEditRoleForm] = useState({ name: '', status: 'active' });
  const [editRoleNameError, setEditRoleNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const roleIds = useMemo(() => roles.map((r) => r.id), [roles]);
  const allRolesSelected = roleIds.length > 0 && roleIds.every((id) => roleSel.selected.has(id));

  const resetRoleForm = () => {
    setRoleForm({ ...BLANK_ROLE_FORM });
    setRoleFieldErrors({ ...BLANK_ROLE_FIELD_ERRORS });
  };

  const applyMasterConflict = (e, fallback, setFieldErrors) => {
    const raw = e?.response?.data?.detail;
    const field = masterDuplicateConflictField(raw);
    if (field) {
      setFieldErrors((prev) => ({ ...prev, [field]: raw }));
      return;
    }
    toast.error(errDetail(e, fallback));
  };

  const handleRoleDialogOpenChange = (open) => {
    if (!open) {
      if (isRoleFormDirty(roleForm)) {
        setRoleOpen(false);
        setDiscardConfirmOpen(true);
        return;
      }
      resetRoleForm();
    }
    setRoleOpen(open);
  };

  const handleDiscardConfirmOpenChange = (open) => {
    setDiscardConfirmOpen(open);
    if (!open) {
      if (discardConfirmedRef.current) {
        discardConfirmedRef.current = false;
        return;
      }
      if (isRoleFormDirty(roleForm)) {
        setRoleOpen(true);
      }
    }
  };

  const confirmDiscardRoleForm = () => {
    discardConfirmedRef.current = true;
    resetRoleForm();
    setRoleOpen(false);
    setDiscardConfirmOpen(false);
  };

  const createRole = async () => {
    setRoleFieldErrors({ ...BLANK_ROLE_FIELD_ERRORS });
    const codeCheck = validateMasterCode(roleForm.code);
    if (!codeCheck.ok) return toast.error(codeCheck.message);
    const codeUnique = validateMasterCodeUnique(codeCheck.value, roles, { entityLabel: 'Role' });
    if (!codeUnique.ok) return setRoleFieldErrors({ code: codeUnique.message, name: '' });
    const nameCheck = validateMasterDisplayName(roleForm.name);
    if (!nameCheck.ok) return toast.error(nameCheck.message);
    const nameUnique = validateMasterNameUnique(nameCheck.value, roles, { entityLabel: 'Role' });
    if (!nameUnique.ok) return setRoleFieldErrors({ code: '', name: nameUnique.message });
    const payload = { ...roleForm, code: codeCheck.value, name: nameCheck.value };
    setSubmitting(true);
    try {
      await v1Post('/master/roles', payload);
      qc.invalidateQueries({ queryKey: ['v1', 'roles'] });
      toast.success(`Role ${payload.name} created.`);
      setRoleOpen(false);
      resetRoleForm();
    } catch (e) {
      applyMasterConflict(e, 'Could not create role.', setRoleFieldErrors);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditRole = (r) => {
    setEditRole(r);
    setEditRoleForm({ name: r.name, status: r.status });
    setEditRoleNameError('');
  };

  const saveRole = async () => {
    if (!editRole) return;
    setEditRoleNameError('');
    const nameCheck = validateMasterDisplayName(editRoleForm.name);
    if (!nameCheck.ok) return toast.error(nameCheck.message);
    const nameUnique = validateMasterNameUnique(nameCheck.value, roles, {
      excludeId: editRole.id,
      entityLabel: 'Role',
    });
    if (!nameUnique.ok) return setEditRoleNameError(nameUnique.message);
    const payload = { ...editRoleForm, name: nameCheck.value };
    setSubmitting(true);
    try {
      await v1Patch(`/master/roles/${editRole.id}`, payload);
      qc.invalidateQueries({ queryKey: ['v1', 'roles'] });
      toast.success(`Role ${payload.name} updated.`);
      setEditRole(null);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      if (masterDuplicateConflictField(raw) === 'name') setEditRoleNameError(raw);
      else toast.error(errDetail(e, 'Could not update role.'));
    } finally {
      setSubmitting(false);
    }
  };

  const askDeleteRole = (r) => {
    onRequestDelete({
      kind: 'role',
      ids: [r.id],
      label: `Delete role “${r.name}”? It will be removed from master lists but kept in the database.`,
    });
  };

  const askBulkDeleteRoles = () => {
    const ids = roleIds.filter((id) => roleSel.selected.has(id));
    if (ids.length === 0) return;
    onRequestDelete({
      kind: 'role',
      ids,
      label: ids.length === 1
        ? 'Delete 1 selected role? It will be removed from master lists but kept in the database.'
        : `Delete ${ids.length} selected roles? They will be removed from master lists but kept in the database.`,
    });
  };

  return (
    <>
      <Card className="card-elevated flex h-full min-h-0 flex-col overflow-hidden">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b p-4">
          <div>
            <p className="text-sm font-semibold">Roles</p>
            <p className="text-xs text-muted-foreground">
              {isLoading ? 'Loading…' : `${roles.length} roles`}
              {roleSel.count > 0 ? ` · ${roleSel.count} selected` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {roleSel.count > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 gap-1.5 text-rose-700 hover:bg-rose-50"
                onClick={askBulkDeleteRoles}
                data-testid="roles-delete-selected"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete All Selected ({roleSel.count})
              </Button>
            )}
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setRoleOpen(true)} data-testid="add-role-button">
              <Plus className="h-4 w-4" /> Add role
            </Button>
          </div>
        </div>
        {isError ? (
          <p className="p-4 text-sm text-destructive" data-testid="roles-error">Could not load roles.</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="sticky top-0 z-10 bg-secondary/40">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allRolesSelected}
                    onCheckedChange={(v) => roleSel.setAll(roleIds, v === true)}
                    disabled={roleIds.length === 0}
                    aria-label="Select all roles"
                    data-testid="roles-select-all"
                  />
                </TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.length === 0 && !isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    No roles yet. Add a role to get started.
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((r) => (
                  <TableRow key={r.id} data-testid="role-row">
                    <TableCell>
                      <Checkbox
                        checked={roleSel.selected.has(r.id)}
                        onCheckedChange={(v) => roleSel.toggle(r.id, v === true)}
                        aria-label={`Select role ${r.name}`}
                        data-testid={`role-select-${r.code}`}
                      />
                    </TableCell>
                    <TableCell><code className="rounded bg-secondary px-1.5 py-0.5 text-xs">{r.code}</code></TableCell>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell><StatusPill status={r.status} size="sm" /></TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEditRole(r)}
                          aria-label={`Edit role ${r.name}`}
                          data-testid="edit-role-button"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-rose-700 hover:bg-rose-50"
                          onClick={() => askDeleteRole(r)}
                          aria-label={`Delete role ${r.name}`}
                          data-testid="delete-role-button"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        )}
      </Card>

      <Dialog open={roleOpen} onOpenChange={handleRoleDialogOpenChange}>
        <DialogContent data-testid="add-role-dialog">
          <DialogHeader>
            <DialogTitle>Add new role</DialogTitle>
            <DialogDescription>Roles are used across candidates, pipelines, and question-bank tagging.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Role code</label>
              <Input
                value={roleForm.code}
                onChange={(e) => {
                  setRoleForm({ ...roleForm, code: e.target.value.toUpperCase() });
                  if (roleFieldErrors.code) setRoleFieldErrors({ ...roleFieldErrors, code: '' });
                }}
                placeholder="e.g. EM"
                aria-invalid={!!roleFieldErrors.code}
                data-testid="role-code-input"
              />
              {roleFieldErrors.code ? (
                <p className="mt-1 text-xs text-destructive" data-testid="role-code-error">{roleFieldErrors.code}</p>
              ) : (
                <p className="mt-1 text-[11px] text-muted-foreground">Short identifier shown in tables and pipeline keys.</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Display name</label>
              <Input
                value={roleForm.name}
                onChange={(e) => {
                  setRoleForm({ ...roleForm, name: e.target.value });
                  if (roleFieldErrors.name) setRoleFieldErrors({ ...roleFieldErrors, name: '' });
                }}
                placeholder="e.g. Engineering Manager"
                aria-invalid={!!roleFieldErrors.name}
                data-testid="role-name-input"
              />
              {roleFieldErrors.name ? (
                <p className="mt-1 text-xs text-destructive" data-testid="role-name-error">{roleFieldErrors.name}</p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={roleForm.status} onValueChange={(v) => setRoleForm({ ...roleForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleRoleDialogOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={createRole} disabled={submitting} data-testid="role-create-confirm">{submitting ? 'Creating…' : 'Create role'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={discardConfirmOpen} onOpenChange={handleDiscardConfirmOpenChange}>
        <AlertDialogContent data-testid="add-role-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>You have unsaved changes. Are you sure you want to discard them?</AlertDialogTitle>
            <AlertDialogDescription>
              Your entered role details will be lost if you discard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="add-role-continue-editing">Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardRoleForm} data-testid="add-role-discard-confirm">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editRole} onOpenChange={(v) => !v && setEditRole(null)}>
        <DialogContent data-testid="edit-role-dialog">
          <DialogHeader>
            <DialogTitle>Edit role</DialogTitle>
            <DialogDescription>Update the display name or status. The role code is immutable once created.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Role code</label>
              <Input value={editRole?.code || ''} disabled data-testid="edit-role-code-input" />
              <p className="mt-1 text-[11px] text-muted-foreground">The code is locked because it is referenced by candidates, pipelines, and question banks.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Display name</label>
              <Input
                value={editRoleForm.name}
                onChange={(e) => {
                  setEditRoleForm({ ...editRoleForm, name: e.target.value });
                  if (editRoleNameError) setEditRoleNameError('');
                }}
                placeholder="e.g. Engineering Manager"
                aria-invalid={!!editRoleNameError}
                data-testid="edit-role-name-input"
              />
              {editRoleNameError ? (
                <p className="mt-1 text-xs text-destructive" data-testid="edit-role-name-error">{editRoleNameError}</p>
              ) : null}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={editRoleForm.status} onValueChange={(v) => setEditRoleForm({ ...editRoleForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRole(null)} disabled={submitting}>Cancel</Button>
            <Button onClick={saveRole} disabled={submitting} data-testid="role-edit-confirm">{submitting ? 'Saving…' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
